import React, { useEffect, useState } from "react";
import {
  PlayerCardData,
  PlayerClubCountryInfoData,
  PlayerClubInfoData,
  PlayerLargeDataText,
  PlayerLargeDataValue,
} from "../../styled-components/PlayerCard.Styles";
import LabelText from "../../ui/LabelText";
import LabelNumber from "../../styled-components/LabelNumber";
import {
  formatNumberToKAndM,
  formatNumberToKAndMwithoutsign,
} from "../../utils/formatNumberToKandM";
import { ModuleSpinner } from "../../ui/LoadingSpinner/ModuleSpinner";
import EmptyDataButton from "../../ui/EmptyDataButton";
import { useTheme } from "../../contexts/ThemeContext";
import { getLeaguePositionDetails } from "../../utils/clubFormationsFunctions";
import { IClubCardDetail } from "../../types/IClubDetail";
import IApiResponse from "../../types/IApiResponse";
import { postData } from "../../services/api";
import apiUrls from "../../config/apiUrls";
import handleApiResponse from "../../utils/handleApiResponse";
import noImageClub from "../../assets/icons/noImageClub.svg";
import noImageFlag from "../../assets/icons/noImageFlag.svg";

interface ClubHeaderPDFProps {
  teamId: number;
  setCurrentClubName?: (clubName: string) => void;
}

const ClubHeaderPDF: React.FC<ClubHeaderPDFProps> = ({ teamId, setCurrentClubName }) => {
  const { theme } = useTheme();
  const [clubData, setClubData] = useState<IClubCardDetail>();
  const [customloading, setLoading] = useState<boolean>();
  const { formattedPosition, expectedPosition } = getLeaguePositionDetails(
    clubData!
  );
  useEffect(() => {
    const fetchClubData = async () => {
      setLoading(true);
      const requestParameters = {
        TeamId: teamId,
      };
      const response: IApiResponse<IClubCardDetail> = await postData(
        apiUrls.GetTeamCardDetails,
        requestParameters
      );
      handleApiResponse(
        response,
        (teamsData) => {
          setLoading(false);
          setClubData(teamsData);
          // Pass the club name to parent component
          if (setCurrentClubName && teamsData?.teamName) {
            setCurrentClubName(teamsData.teamName);
          }
        },
        (error) => {
          setLoading(false);
          console.error("Error fetching club card Data:", error);
        }
      );
    };
    fetchClubData();
  }, [teamId, setCurrentClubName]);
  const currentValueDiffStyle =
    clubData?.currentValue !== 0
      ? {
          background: clubData?.currentValue! > 0 ? "#4AF075" : "#FF3864",
          border: "none",
          borderRadius: "0px",
        }
      : {};
  const valueDiffBorder =
    clubData?.currentValue !== 0
      ? {
          border:
            clubData?.currentValue! > 0
              ? "1px solid #4AF075"
              : "1px solid #FF3864",
        }
      : {};
  const profitLossStyled =
    clubData?.totalProfitLoss !== 0 && clubData?.totalProfitLoss !== null
      ? {
          background: clubData?.totalProfitLoss! > 0 ? "#4AF075" : "#FF3864",
        }
      : {};
  return (
    <div className="player-card-data-main">
      <PlayerCardData className="player-card-data">
        <div className="player-card-detail">
          <div className="player-header">
            <img
              src={clubData?.teamLogoURL ? clubData?.teamLogoURL : noImageClub}
              alt="club-logo"
              width={140}
              height={140}
            />
          </div>
          <div className="player-name-header">
            <div className="clubName-position-container">
              <LabelText
                className="player-detail-given-name"
                style={{ color: theme.NumberColor }}
              >
                {clubData?.competitionName}
              </LabelText>
              {clubData?.currentLeaguePosition !== 0 &&
                clubData?.currentLeaguePosition !== null && (
                  <LabelText
                    className="player-detail-given-name"
                    style={{ color: theme.NumberColor }}
                  >
                    {"  • Currently " + formattedPosition}{" "}
                  </LabelText>
                )}
              {(clubData?.currentLeaguePosition === 0 ||
                clubData?.currentLeaguePosition === null) &&
                clubData?.currentExpectedLeaguePosition && (
                  <LabelText
                    className="player-detail-given-name"
                    style={{ color: theme.NumberColor }}
                  >
                    {"  • Expected league pos. " + expectedPosition}{" "}
                  </LabelText>
                )}
            </div>
            <div
              className="player-name-squad"
              style={{ paddingTop: "0px", width: "630px" }}
            >
              <LabelText className="player-detail-family-name">
                {clubData?.teamName}
              </LabelText>
            </div>
            <div style={{ alignSelf: "flex-end", marginTop: "-30px" }}>
              <LabelNumber className="stat-row-value">Manager:</LabelNumber>
              <LabelText className="player-given-name">
                {clubData?.manager}
              </LabelText>
            </div>
            <div className="player-clubInfo" style={{ paddingLeft: "0px" }}>
              <PlayerClubCountryInfoData>
                <PlayerClubInfoData
                  country="country"
                  className="player-descriptive-data"
                >
                  <PlayerLargeDataText className="descriptive-text">
                    Country
                  </PlayerLargeDataText>
                  <PlayerLargeDataValue className="descriptive-value">
                    {clubData?.countryName}
                  </PlayerLargeDataValue>
                </PlayerClubInfoData>
                <img
                  src={
                    clubData?.countryCode
                      ? `/assets/country_flags/${
                          clubData?.countryCode || ""
                        }.svg`
                      : noImageFlag
                  }
                  alt="Country Flag"
                  className="country-image"
                />
              </PlayerClubCountryInfoData>
              <PlayerClubInfoData className="player-descriptive-data">
                <PlayerLargeDataText className="descriptive-text">
                  Revenue per season
                </PlayerLargeDataText>
                <PlayerLargeDataValue className="descriptive-value">
                  €{formatNumberToKAndM(clubData?.totalRevenue!)}
                </PlayerLargeDataValue>
              </PlayerClubInfoData>
              <PlayerClubInfoData className="player-descriptive-data">
                <PlayerLargeDataText className="descriptive-text">
                  Costs per season
                </PlayerLargeDataText>
                <PlayerLargeDataValue className="descriptive-value">
                  €{formatNumberToKAndM(clubData?.totalCosts!)}
                </PlayerLargeDataValue>
              </PlayerClubInfoData>
              <PlayerClubInfoData
                className="player-descriptive-data"
                style={profitLossStyled}
              >
                <LabelNumber className="descriptive-large-text">
                  Profit/loss
                </LabelNumber>
                <LabelNumber className="descriptive-large-value">
                  {clubData?.totalProfitLoss ? (
                    `${
                      clubData?.totalProfitLoss >= 0 ? "+" : "-"
                    }€${formatNumberToKAndMwithoutsign(
                      clubData?.totalProfitLoss
                    )}`
                  ) : (
                    <EmptyDataButton label="Data not provided by club" />
                  )}
                </LabelNumber>
              </PlayerClubInfoData>
              <div
                style={{
                  display: "flex",
                  borderRadius: "4px",
                  ...valueDiffBorder,
                }}
              >
                <PlayerClubInfoData
                  className="player-descriptive-data"
                  style={{ border: "none" }}
                >
                  <LabelNumber className="descriptive-large-text">
                    Squad purchase cost
                  </LabelNumber>
                  <LabelNumber className="descriptive-large-value">
                    {clubData?.totalPurchaceCost ? (
                      `€${formatNumberToKAndMwithoutsign(
                        clubData?.totalPurchaceCost
                      )}`
                    ) : (
                      <EmptyDataButton label="Data not provided by club" />
                    )}
                  </LabelNumber>
                </PlayerClubInfoData>
                <PlayerClubInfoData
                  className="player-descriptive-data"
                  style={currentValueDiffStyle}
                >
                  <LabelNumber className="descriptive-large-text">
                    vs current value
                  </LabelNumber>
                  <LabelNumber className="descriptive-large-value">
                    {clubData?.currentValue ? (
                      `${
                        clubData?.currentValue >= 0 ? "+" : "-"
                      }€${formatNumberToKAndMwithoutsign(
                        clubData?.currentValue
                      )}`
                    ) : (
                      <EmptyDataButton label="Data not provided by club" />
                    )}
                  </LabelNumber>
                </PlayerClubInfoData>
              </div>
            </div>
          </div>
        </div>
      </PlayerCardData>
      {customloading && <ModuleSpinner />}
    </div>
  );
};

export default ClubHeaderPDF;
